import { apiPost } from "./api";
import type { PortalConfig } from "./config";
import { money } from "./format";

interface Quote { ticketNo: number; method: string; cost: number }
interface Problem { ticketNo: number; error: string }
interface PrintResult { url: string | null; labels: number; tickets: number; awaitingPickup: number[]; noShippingLabel: number[]; noReturnLabel: number[]; failed: number[] }

const QUOTE_BATCH = 12;
const BUY_BATCH = 4;
const chunks = <T,>(a: T[], n: number) => Array.from({ length: Math.ceil(a.length / n) }, (_, i) => a.slice(i * n, i * n + n));
const plural = (k: number, word: string) => `${k} ${word}${k === 1 ? "" : "s"}`;
const list = (nos: number[]) => `#${nos.join(", #")}`;

/**
 * Print Shipping Labels (ticket list and ticket page, owner 2026-11-03): buys labels 2 and 3 through
 * Shippo for tickets that have none (after a confirmation with the count and cost), then prints labels
 * 2 and 3 for every ticket; unshipped tickets move to Awaiting Pickup. `tab` is opened by the caller
 * at click time, so the browser does not block it.
 */
export async function printShippingLabels(config: PortalConfig, token: string, ticketNos: number[], tab: Window | null,
  progress: (text: string) => void = () => undefined): Promise<string> {
  try {
    progress("Checking labels…");
    const toBuy: Quote[] = [];
    const problems: Problem[] = [];
    let testMode = true;
    for (const batch of chunks(ticketNos, QUOTE_BATCH)) {
      const q = await apiPost<{ toBuy: Quote[]; problems: Problem[]; testMode: boolean }>(config, token, "/tickets/actions/label-quote", { ticketNos: batch });
      toBuy.push(...q.toBuy);
      problems.push(...q.problems);
      testMode = q.testMode;
    }
    let bought = 0;
    if (toBuy.length) {
      const total = toBuy.reduce((s, q) => s + q.cost, 0);
      const lines = toBuy.slice(0, 12).map((q) => `#${q.ticketNo}: ${q.method}, ${money(q.cost)}`).join("\n");
      const ok = window.confirm(`Buy shipping labels for ${plural(toBuy.length, "ticket")} through Shippo?\n\n${lines}${toBuy.length > 12 ? "\n…" : ""}\n\n` +
        `Total about ${money(total)} (label 2 to the customer plus the spare return label).` +
        (testMode ? "\n\nTest mode: these are Shippo test labels, nothing is charged and they cannot be used." : ""));
      if (!ok) { tab?.close(); return "Nothing printed: no labels were bought."; }
      let done = 0;
      for (const batch of chunks(toBuy.map((q) => q.ticketNo), BUY_BATCH)) {
        progress(`Buying labels… ${done}/${toBuy.length}`);
        const r = await apiPost<{ bought: number[]; problems: Problem[] }>(config, token, "/tickets/actions/buy-labels", { ticketNos: batch });
        bought += r.bought.length;
        problems.push(...r.problems);
        done += batch.length;
      }
    }
    progress("Preparing the PDF…");
    const p = await apiPost<PrintResult>(config, token, "/tickets/actions/labels", { ticketNos });
    if (p.url && tab) tab.location.href = p.url;
    else tab?.close();
    const parts = [
      bought ? `Bought labels for ${plural(bought, "ticket")}.` : "",
      p.labels ? `Printed ${plural(p.labels, "label")} for ${plural(p.tickets, "ticket")}.` : "Nothing to print.",
      p.awaitingPickup.length ? `${plural(p.awaitingPickup.length, "ticket")} moved to Awaiting Pickup.` : "",
      problems.length ? `Problems: ${problems.map((x) => `#${x.ticketNo} ${x.error}`).join("; ")}` : "",
      p.failed.length ? `Could not download: ${list(p.failed)}.` : "",
      p.url && !tab ? `Open the PDF: ${p.url}` : "",
    ];
    return parts.filter(Boolean).join(" ");
  } catch (e) {
    tab?.close();
    throw e;
  }
}
